import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';

function getLegacyRedirectTarget(restPath?: string): string {
  if (!restPath) return '/dashboard';

  // Remove any leading/trailing slashes
  let cleanPath = restPath.replace(/^\/+|\/+$/g, '');

  // Strip leading "whatsapp/" if present
  if (cleanPath.startsWith('whatsapp/')) {
    cleanPath = cleanPath.substring('whatsapp/'.length);
  }

  // 1. Flat root routes
  const flatPrefixes = [
    'dashboard',
    'live-chat',
    'contacts',
    'appointment',
    'plans',
    'payment-history',
    'affiliate',
    'ai-chatbot'
  ];
  if (flatPrefixes.some(prefix => cleanPath.startsWith(prefix))) {
    return `/${cleanPath}`;
  }

  // 2. Automations routes
  const automationsPrefixes = [
    'workflows',
    'drip-campaigns',
    'reminders',
    'attributes'
  ];
  if (automationsPrefixes.some(prefix => cleanPath.startsWith(prefix))) {
    // Strip workflows prefix for subpaths if needed
    const normalized = cleanPath.replace(/^workflows\/(flow-builder|api-management|library)/, '$1');
    return `/automations/${normalized}`;
  }

  // 3. WhatsApp CRM routes (templates, campaigns, api-integration, api-keys, opt-inout-settings)
  return `/whatsapp/${cleanPath}`;
}

export default function LegacyRedirect() {
  const { id, '*': restPath } = useParams<{ id: string; '*': string }>();
  const { initializeAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const isValidObjectId = id && /^[0-9a-fA-F]{24}$/.test(id);
    let targetPath = '';
    if (id && isValidObjectId) {
      targetPath = getLegacyRedirectTarget(restPath);
    } else {
      const fullPath = id + (restPath ? '/' + restPath : '');
      targetPath = getLegacyRedirectTarget(fullPath);
    }
    navigate(targetPath, { replace: true });
  }, [id, restPath, initializeAuth, navigate]);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
    </div>
  );
}

export function FlatToWhatsAppRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const path = window.location.pathname;
    const target = `/whatsapp${path}`;
    navigate(target, { replace: true });
  }, [navigate]);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
    </div>
  );
}

export function FlatToAutomationsRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const path = window.location.pathname;
    let cleanPath = path;
    if (cleanPath.startsWith('/whatsapp')) {
      cleanPath = cleanPath.substring('/whatsapp'.length);
    }
    // Clean workflows nesting for flow-builder, api-management, and library
    cleanPath = cleanPath.replace(/^\/workflows\/(flow-builder|api-management|library)/, '/$1');
    const target = `/automations${cleanPath}`;
    navigate(target, { replace: true });
  }, [navigate]);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
    </div>
  );
}
